import { PdfIcon } from '@p2p/assets/svg';
import { Button, FileUpload, Typography } from '@p2p/components';
import { useGetMerchantOrderQuery, useUploadMatchingReceiptsMutation } from '@p2p/services';
import { MSP_PAGE, UPLOADED_ORDER_LOCAL_STORAGE_KEY } from '@p2p/shared/constants';
import { getFirstErrorMessage } from '@p2p/shared/functions';
import { useState } from 'react';
import { ErrorResponse, useNavigate, useParams } from 'react-router-dom';

import { PaymentTimer } from '../PaymentTimer/PaymentTimer';
import styles from './BillBlock.module.scss';
import { BillBlockProps } from './BillBlock.types';

const DEFAULT_UPLOAD_ERROR = 'Не удалось загрузить чек. Попробуйте отправить его ещё раз.';

/**
 * P2P-2742: раньше в блок ошибки попадало первое значение из ответа как есть, и
 * игрок видел служебные коды ("CONFLICT" при повторном нажатии в течение 30 секунд,
 * "FETCH_ERROR" при обрыве мобильной сети). Показываем понятный текст.
 */
const getUploadErrorMessage = (error: unknown): string => {
  const { status, data } = (error ?? {}) as { status?: number | string; data?: unknown };

  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
    return 'Не удалось отправить чек: проблема со связью. Проверьте интернет и попробуйте ещё раз.';
  }

  if (status === 409) {
    return 'Чек уже отправляется, подождите несколько секунд.';
  }

  if (status === 413) {
    return 'Файл слишком большой. Загрузите чек размером до 2 МБ.';
  }

  const message = getFirstErrorMessage(data ?? error);

  // Служебные коды вида ORDER_UPLOAD_MSP_RECEIPT_IN_PROGRESS игроку ничего не говорят.
  if (!message || /^[A-Z0-9_]+$/.test(message)) {
    return DEFAULT_UPLOAD_ERROR;
  }

  return message;
};

export const BillBlock = ({ isShortView, onUploaded, resetFileOnBadRequest }: BillBlockProps) => {
  const { id = '' } = useParams<{ id: string }>();
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState('');
  const { data: order } = useGetMerchantOrderQuery(id, { skip: !id });
  const [uploadMatchingReceipts, { isLoading: isUploading }] = useUploadMatchingReceiptsMutation();
  const navigate = useNavigate();

  const handleRemoveFile = () => {
    setFiles([]);
    setError('');
  };

  const handleSendFile = () => {
    // Повторный клик до ответа бэка не должен отправить чек второй раз.
    if (isUploading) return;

    setError('');

    const [file] = files;
    if (!file) {
      setError('Файл не выбран.');
      return;
    }

    const formData = new FormData();
    formData.append('files', file);

    uploadMatchingReceipts({ id, body: formData })
      .unwrap()
      .then(() => {
        setError('');
        setFiles([]);

        localStorage.setItem(UPLOADED_ORDER_LOCAL_STORAGE_KEY, id);
        if (onUploaded) {
          onUploaded();
        } else {
          navigate(`${MSP_PAGE}/${id}/search`);
        }
      })
      .catch((err: ErrorResponse) => {
        // P2P-2742: по умолчанию файл НЕ сбрасываем -- иначе после сетевой ошибки
        // игроку приходится заново искать чек в банковском приложении. proof_page
        // (P2P-2880) сбрасывает его только при отказе бэка (400), не при обрыве связи.
        setError(getUploadErrorMessage(err));

        if (resetFileOnBadRequest && (err as { status?: unknown })?.status === 400) {
          setFiles([]);
        }
      });
  };

  return (
    <>
      <Typography variant="h3" color="heading" className={styles.title}>
        После успешного перевода – загрузите чек
      </Typography>
      <Typography variant="text-m-medium">Загрузите чек в формате PDF.</Typography>
      <Typography variant="text-m-medium" className={styles.commonMarginBottom}>
        Это необходимо для завершения операции.
      </Typography>
      {!isShortView && (
        <PaymentTimer time={order?.time_left ?? 0} className={styles.commonMarginBottom} />
      )}
      {files.length === 0 ? (
        <FileUpload
          files={files}
          setFiles={setFiles}
          error={error}
          setError={setError}
          allowedFormats={['pdf']}
          maxCountFiles={1}
          maxFileSize={2}
          hideTitle
        />
      ) : (
        <div className={styles.uploadArea}>
          <PdfIcon className={styles.pdfIcon} />
          <Button
            variant="default"
            onClick={handleSendFile}
            disabled={isUploading}
            isLoading={isUploading}
          >
            Отправить
          </Button>
          <Button variant="outlined" onClick={handleRemoveFile} disabled={isUploading}>
            Удалить
          </Button>
        </div>
      )}
      {!!error && files.length > 0 && (
        <Typography variant="text-s-medium" color="uploader-area-error-text-error">
          {error}
        </Typography>
      )}
    </>
  );
};
